import React, { useState, useEffect, useImperativeHandle, forwardRef, useId } from 'react';
import PillToggle from '.././PillToggle';
import ToggleSwitch from '.././ToggleSwitch';
import ScopeSelector from './ScopeSelector';
import Loading from '.././Loading';
import type { IDigestAlertPreference } from '../../types/api-caller';

export interface DigestSettingsHandle {
  buildPayload: () => IDigestAlertPreference;
}

interface DigestSettingsProps {
  initialPreference: IDigestAlertPreference | null;
  onDirty: () => void;
}

const DigestSettings = forwardRef<DigestSettingsHandle, DigestSettingsProps>(({ initialPreference, onDirty }, ref) => {
  const [digestEnabled, setDigestEnabled] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<string>('daily');
  const [excludeLowArticlesForDigest, setExcludeLowArticlesForDigest] = useState<boolean>(false);
  const [portfolioDigestEnabled, setPortfolioDigestEnabled] = useState<boolean>(true);
  const [allListsEnabledForDigest, setAllListsEnabledForDigest] = useState<boolean>(false);
  const [selectedListsForDigest, setSelectedListsForDigest] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const digestLowArticlesId = useId();

  useEffect(() => {
    setLoading(true);

    if (!initialPreference) {
      setDigestEnabled(false);
      setFrequency('daily');
      setExcludeLowArticlesForDigest(false);
      setPortfolioDigestEnabled(true);
      setAllListsEnabledForDigest(false);
      setSelectedListsForDigest([]);
      setLoading(false);
      return;
    }

    setDigestEnabled(initialPreference.enabled);
    setFrequency(initialPreference.frequency.toLowerCase());
    setExcludeLowArticlesForDigest(initialPreference.exclude_low_articles ?? false);

    if (initialPreference.scope_type === 'PORTFOLIO') {
      setPortfolioDigestEnabled(true);
      setAllListsEnabledForDigest(false);
      setSelectedListsForDigest([]);
    } else if (initialPreference.scope_type === 'ALL_LISTS') {
      setPortfolioDigestEnabled(false);
      setAllListsEnabledForDigest(true);
      setSelectedListsForDigest([]);
    } else {
      setPortfolioDigestEnabled(false);
      setAllListsEnabledForDigest(false);
      setSelectedListsForDigest(initialPreference.list_ids || []);
    }

    setLoading(false);
  }, []);

  function handleDigestEnabledChange(checked: boolean): void {
    setDigestEnabled(checked);
    onDirty();
  }

  function handleFrequencyChange(val: string): void {
    setFrequency(val);
    onDirty();
  }

  function handleExcludeLowArticlesForDigestChange(checked: boolean): void {
    setExcludeLowArticlesForDigest(checked);
    onDirty();
  }

  function handlePortfolioDigestEnabledChange(checked: boolean): void {
    if (!checked && !allListsEnabledForDigest && selectedListsForDigest.length === 0) {
      return;
    }

    setPortfolioDigestEnabled(checked);
    if (checked) {
      setAllListsEnabledForDigest(false);
      setSelectedListsForDigest([]);
    }
    onDirty();
  }

  const handleDigestAllListsChange = (checked: boolean) => {
    if (!checked && !portfolioDigestEnabled && selectedListsForDigest.length === 0) {
      return;
    }

    setAllListsEnabledForDigest(checked);
    if (checked) {
      setSelectedListsForDigest([]);
      setPortfolioDigestEnabled(false);
    }
    onDirty();
  };

  const handleDigestListsChange = (listId: number, checked: boolean, allListsChecked: boolean) => {
    if (allListsEnabledForDigest && !portfolioDigestEnabled) {
      setAllListsEnabledForDigest(false);
      setSelectedListsForDigest([listId]);
      setPortfolioDigestEnabled(false);
    } else {
      if (checked) {
        if (allListsChecked) {
          setAllListsEnabledForDigest(true);
          setSelectedListsForDigest([]);
          setPortfolioDigestEnabled(false);
        } else {
          setSelectedListsForDigest([...selectedListsForDigest, listId]);
          setPortfolioDigestEnabled(false);
          setAllListsEnabledForDigest(false);
        }
      } else {
        setSelectedListsForDigest(selectedListsForDigest.filter((l) => l !== listId));
      }
    }
    onDirty();
  };

  useImperativeHandle(ref, () => ({
    buildPayload(): IDigestAlertPreference {
      let scope_type: 'PORTFOLIO' | 'ALL_LISTS' | 'SELECTED_LISTS';
      let list_ids: number[] | undefined;

      if (portfolioDigestEnabled) {
        scope_type = 'PORTFOLIO';
        list_ids = [];
      } else if (allListsEnabledForDigest) {
        scope_type = 'ALL_LISTS';
        list_ids = [];
      } else {
        scope_type = 'SELECTED_LISTS';
        list_ids = selectedListsForDigest;
      }

      return {
        enabled: digestEnabled,
        frequency: frequency.toUpperCase(), // 'DAILY' or 'WEEKLY'
        scope_type,
        exclude_low_articles: excludeLowArticlesForDigest,
        list_ids,
      };
    },
  }));

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between items-center gap-4">
        <div>
          Receive a daily or weekly summary email showing companies from your portfolio or lists that have seen an fss score change in the
          last 24 hour or in the last 7 days.
        </div>
        <ToggleSwitch checked={digestEnabled} onChange={handleDigestEnabledChange} />
      </div>
      {digestEnabled && (
        <div>
          <hr />
          <div className="flex flex-row gap-4">
            <div className="flex-1 flex flex-col gap-2">
              <h2 className="text-ow-primary text-xl font-bold mb-2">Frequency</h2>
              <div className="flex flex-col gap-2">
                <PillToggle
                  options={[
                    { label: 'Daily', value: 'daily' },
                    { label: 'Weekly', value: 'weekly' },
                  ]}
                  selected={frequency}
                  onChange={handleFrequencyChange}
                />
                <div className="grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-3 items-center">
                  <input
                    id={digestLowArticlesId}
                    type="checkbox"
                    checked={excludeLowArticlesForDigest}
                    onChange={(e) => handleExcludeLowArticlesForDigestChange(e.target.checked)}
                    className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer row-span-1"
                  />
                  <label htmlFor={digestLowArticlesId} className="font-medium text-base text-black cursor-pointer self-center">
                    Remove companies with ‘low article’ scores from emails
                  </label>
                  <div />
                  <p className="text-gray-400 text-sm col-start-2 pr-8 pt-1">
                    This will remove companies from your daily/weekly emails that have ‘low article’ volumes, which may dominate top 20
                    email notifications because they can have bigger swings in percentage score changes.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <div>
                <h2 className="text-ow-primary text-xl font-bold mb-2">Scope</h2>
                <p className="text-ow-darkbg-grey">Select from either Portfolio or Lists.</p>
              </div>
              <ScopeSelector
                portfolioEnabled={portfolioDigestEnabled}
                allListsEnabled={allListsEnabledForDigest}
                selectedLists={selectedListsForDigest}
                onPortfolioChange={handlePortfolioDigestEnabledChange}
                onAllListsChange={handleDigestAllListsChange}
                onListsChange={handleDigestListsChange}
                sectionId="digest"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DigestSettings;
